import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoginModal from './components/Login/LoginModal';
import RegisterModal from './components/Login/RegisterModal';
import CourtDetails from './components/Court/CourtDetails';
import HomePage from './components/Homepage/HomePage';
import SearchResults from './components/Homepage/SearchResults';
import AppUserProfile from './components/Profile/AppUserProfile';
import UserProfile from './components/Profile/UserProfile';
import AboutUs from './components/About/AboutUs';
import PWAInstallBanner from './components/PWA/PWAInstallBanner';
import OfflineIndicator from './components/PWA/OfflineIndicator';
import { getToken, logout as logoutHelper, getUserProfile } from './api';
import { initializePWA } from './utils/pwa';

function App() {
  const [user, setUser] = useState(null);
  const [courts, setCourts] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const handleLogout = () => {
    logoutHelper();
    setUser(null);
    setAuthLoading(false); // Ensure we're not stuck in loading state
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  // Load user profile on app start if token exists
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = getToken();
        if (token) {
          try {
            const data = await getUserProfile();
            if (!data.error && data.user) {
              setUser(data.user);
            } else {
              // Token might be expired or invalid, remove it
              console.warn('Failed to load user profile, removing token');
              localStorage.removeItem('token');
            }
          } catch (error) {
            console.error('Error loading user profile:', error);
            // Remove potentially invalid token
            localStorage.removeItem('token');
          }
        }

        // Initialize PWA features
        initializePWA();

      } catch (error) {
        console.error('Critical error during app initialization:', error);
      } finally {
        // Always set loading to false after attempting to load
        setAuthLoading(false);
      }
    };

    loadUserProfile();
  }, []); // Remove user dependency to prevent re-runs

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <div>
          <nav
            className="app-nav py-3 rounded-4 d-flex align-items-center justify-content-between flex-nowrap"
          >
            <Link className="icon-logo-navbar flex-shrink-0" to="/">
              <img src="/icon-logo.svg" alt="RYC logo" className="app-logo" />
            </Link>

            <div className="buttons-navbar d-flex align-items-center flex-shrink-0">
              {authLoading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : user ? (
                <div className='d-flex gap-2'>
                  <AppUserProfile user={user} onLogout={handleLogout} />
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn login-button"
                    onClick={() => setShowLoginModal(true)}
                  >
                    Login
                  </button>
                  <button
                    className="btn register-button"
                    onClick={() => setShowRegisterModal(true)}
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </nav>

          <Routes>
            <Route path="/" element={<HomePage courts={courts} />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/courts/:id" element={<CourtDetailsWrapper user={user} />} />
            <Route path="/profile" element={<UserProfile user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/courtlist" />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>

          <LoginModal
            show={showLoginModal}
            onHide={() => setShowLoginModal(false)}
            onLogin={(userData) => {
              setUser(userData);
              setShowLoginModal(false);
              setAuthLoading(false); // Ensure loading state is reset
            }}
          />

          <RegisterModal show={showRegisterModal}
            onHide={() => setShowRegisterModal(false)}
            onRegister={(userData) => {
              setUser(userData);
              setShowRegisterModal(false);
              setAuthLoading(false); // Ensure loading state is reset
            }}
          />

          <footer
            style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#f9fafb',
              color: '#6b7280',
              fontSize: '0.9rem',
            }}
          >
            Developed by <a href="https://instagram.com/jdoseph">Joseph Do</a>  © 2025
          </footer>

          {/* PWA Components */}
          <OfflineIndicator />
          <PWAInstallBanner />

        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

function CourtDetailsWrapper({ user }) {
  const { id } = useParams();
  if (!id || id.trim().length === 0) return <p>Invalid court ID.</p>;
  return <CourtDetails courtId={id} user={user} />;
}

export default App;
